import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { RATIONAL_GENERAL_META, type RationalGeneralParams } from './rational-general.model';

/** Rational general params with index signature for JsxParamStore compatibility */
type RationalGeneralParamsIndexed = RationalGeneralParams & { [key: string]: number };

const EPSILON = 1e-12;

/**
 * Creates general rational functions: (ax + b) / (cx + d)
 *
 * Parameters:
 * - a: numerator linear coefficient
 * - b: numerator constant
 * - c: denominator linear coefficient
 * - d: denominator constant
 */
export class RationalGeneralCreator extends BaseFunctionCreator<RationalGeneralParamsIndexed> {
  readonly functionType = 'rational-general';
  readonly defaultParams: RationalGeneralParamsIndexed = { ...RATIONAL_GENERAL_META.defaultParams };
  readonly availableFeatures = RATIONAL_GENERAL_META.availableFeatures;
  readonly handleConfigs = RATIONAL_GENERAL_META.handleConfigs;
  override readonly computedValueKeys = ['verticalAsymptote', 'horizontalAsymptote', 'yIntercept'] as const;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as RationalGeneralParamsIndexed;

    const colorName = (display?.curve?.color ?? 'purple') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: RATIONAL_GENERAL_META.defaultRanges.a },
      b: { value: initialParams.b, range: RATIONAL_GENERAL_META.defaultRanges.b },
      c: { value: initialParams.c, range: RATIONAL_GENERAL_META.defaultRanges.c },
      d: { value: initialParams.d, range: RATIONAL_GENERAL_META.defaultRanges.d },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const b = paramStore.getValue('b');
      const c = paramStore.getValue('c');
      const d = paramStore.getValue('d');
      const denom = c * x + d;
      if (Math.abs(denom) < EPSILON) {
        return NaN;
      }
      return (a * x + b) / denom;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    // f(x) = (ax + b) / (cx + d)
    // f'(x) = (ad - bc) / (cx + d)²
    const getDerivative = (order = 1): ((x: number) => number) => {
      return (x: number) => {
        const a = paramStore.getValue('a');
        const b = paramStore.getValue('b');
        const c = paramStore.getValue('c');
        const d = paramStore.getValue('d');
        const denom = c * x + d;
        if (Math.abs(denom) < EPSILON) {
          return NaN;
        }
        const det = a * d - b * c;
        // nth derivative: (-1)^(n+1) * n! * c^(n-1) * det / (cx+d)^(n+1)
        let factorial = 1;
        for (let i = 2; i <= order; i++) {
          factorial *= i;
        }
        const sign = order % 2 === 0 ? -1 : 1;
        return (sign * factorial * Math.pow(c, order - 1) * det) / Math.pow(denom, order + 1);
      };
    };

    return {
      id,
      type: 'rational-general',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as RationalGeneralParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      getComputedValues: () => ({
        verticalAsymptote: () => -paramStore.getValue('d') / paramStore.getValue('c'),
        horizontalAsymptote: () => paramStore.getValue('a') / paramStore.getValue('c'),
        yIntercept: () => paramStore.getValue('b') / paramStore.getValue('d'),
      }),
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
